// Pure merge logic for the P1-18 Phase 3 features autocomplete.
//
// Combines two sources into a single ranked suggestion list for the
// chip editor dropdown:
//
//   1. Recent-use (device-local, from localStorage) — the user's own
//      most-recent feature strings, most-recent first.
//   2. Catalog-wide (server, from /catalog/features/suggest) — the
//      most-used feature strings across all firearm items.
//
// Rules:
//   - Dedup case-insensitively (so "Optic Ready" and "optic ready"
//     count as the same entry).
//   - Exclude features that are already on the current item (we don't
//     want the editor to re-suggest something the user already added).
//   - Filter by input substring (case-insensitive) — if the user has
//     typed anything, both sources are filtered before being merged.
//   - Recent-use wins the top of the list up to `recentCap`; catalog
//     fills the rest up to `totalCap`.
//
// Pure — no I/O. Both callers (device UI live, and future preview /
// test code) share the same ranking.

export type SuggestionMergeInput = {
  recent: readonly string[];
  catalog: ReadonlyArray<{ value: string; count: number }>;
  // Features already on the item being edited; excluded from the result.
  currentFeatures: readonly string[];
  // User's current input in the "+ Add" chip. Empty string = no filter.
  query: string;
  recentCap: number;
  totalCap: number;
};

export type SuggestionItem = {
  value: string;
  source: "recent" | "catalog";
  // Only present on catalog items (recent-use has no count).
  count?: number;
};

/**
 * Merge recent-use and catalog-wide feature strings into a single ranked
 * list for the chip-editor dropdown. See file header for rules.
 */
export function mergeFeatureSuggestions(input: SuggestionMergeInput): SuggestionItem[] {
  const excluded = new Set(input.currentFeatures.map((f) => f.toLowerCase()));
  const query = input.query.trim().toLowerCase();
  const matches = (s: string): boolean => query.length === 0 || s.toLowerCase().includes(query);

  const result: SuggestionItem[] = [];
  const seen = new Set<string>();

  // Recent-use first — up to recentCap entries, filtered + de-duplicated
  // against the current features.
  for (const r of input.recent) {
    if (result.length >= input.recentCap) break;
    const key = r.toLowerCase();
    if (seen.has(key) || excluded.has(key)) continue;
    if (!matches(r)) continue;
    result.push({ value: r, source: "recent" });
    seen.add(key);
  }

  // Catalog-wide next — fill up to totalCap. Already-ranked by caller
  // (frequency desc), so we just iterate and dedup.
  for (const c of input.catalog) {
    if (result.length >= input.totalCap) break;
    const key = c.value.toLowerCase();
    if (seen.has(key) || excluded.has(key)) continue;
    if (!matches(c.value)) continue;
    result.push({ value: c.value, source: "catalog", count: c.count });
    seen.add(key);
  }

  return result;
}
